// Where a new account came from: the channel tag on the link (?src=hn), the site that referred
// them, and the page they landed on. First touch only.
//
// Captured on the FIRST page load, before sign-in: the sign-in redirect drops the query string, and
// someone may arrive today and sign up tomorrow. Sent once, after sign-in, to POST /me/attribution;
// the backend keeps it only for accounts created in the last few days, so an existing user who
// clicks a tagged link is never counted as a sign-up.
//
// The marketing site passes its own first touch along on links into the Studio (src, ref, landing),
// so a visitor who read the site first is credited to the channel that brought them to the SITE.
// Someone who installs from the terminal never clicks such a link: the plugin's sign-in opens the
// Studio directly. For them the site also leaves the same first touch in a cookie shared across
// assertion-ai.com (`assertion_attr`), read here when the URL carries nothing.
//
// A Reddit ad click also brings Reddit's click ID (`rdt_cid`). It goes to the backend with the
// rest, which reports the sign-up and plugin connection back to Reddit Ads for that account only.
import { postAttribution } from "./api";

const KEY = "assertionAttribution";
const COOKIE = "assertion_attr";
const MAX_AGE_MS = 30 * 24 * 3600 * 1000;
const OWN_HOSTS = ["assertion-ai.com"];

function isOwnHost(host) {
  const h = (host || "").toLowerCase();
  return OWN_HOSTS.some((o) => h === o || h.endsWith(`.${o}`));
}

function read() {
  try {
    const rec = JSON.parse(localStorage.getItem(KEY) || "null");
    if (rec && Date.now() - (rec.at || 0) < MAX_AGE_MS) return rec;
  } catch (_) {}
  return null;
}

function write(rec) {
  try { localStorage.setItem(KEY, JSON.stringify(rec)); } catch (_) {}
}

// The site's first touch from the shared cookie, or null.
function fromCookie(cookie) {
  try {
    const part = (cookie || "").split("; ").find((c) => c.startsWith(`${COOKIE}=`));
    if (!part) return null;
    const c = JSON.parse(decodeURIComponent(part.slice(COOKIE.length + 1)));
    if (!c || typeof c !== "object" || Date.now() - (c.at || 0) > MAX_AGE_MS) return null;
    return c;
  } catch (_) {
    return null;
  }
}

// Reads the current URL, then the site's cookie, then the referrer. Keeps the first record it
// sees; returns what is stored.
export function captureAttribution(loc = window.location, referrer = document.referrer, cookie = document.cookie) {
  const existing = read();
  if (existing) return existing;
  const q = new URLSearchParams(loc.search || "");
  let src = q.get("src") || q.get("utm_source") || "";
  let ref = q.get("ref") || "";
  let landing = q.get("landing") || "";
  let rdtCid = q.get("rdt_cid") || "";
  if (!src && !ref) {
    const c = fromCookie(cookie);
    if (c) {
      src = String(c.src || "");
      ref = String(c.ref || "");
      landing = String(c.landing || "");
      rdtCid = String(c.rdt_cid || "");
    }
  }
  if (!ref && referrer) {
    try {
      const host = new URL(referrer).hostname;
      if (!isOwnHost(host)) ref = host;
    } catch (_) {}
  }
  if (!src && !ref) return null;
  const rec = { src: src.slice(0, 80), ref: ref.slice(0, 200), landing: (landing || loc.pathname || "").slice(0, 200),
                at: Date.now(), sent: false };
  if (rdtCid) rec.rdt_cid = rdtCid.slice(0, 200);
  write(rec);
  return rec;
}

// Once signed in: send the stored first touch, once. A failed send is retried on a later load.
let inFlight = false;
export async function sendAttribution() {
  const rec = read();
  if (!rec || rec.sent || inFlight) return false;
  inFlight = true;
  try {
    await postAttribution({ src: rec.src, ref: rec.ref, landing: rec.landing, ...(rec.rdt_cid ? { rdt_cid: rec.rdt_cid } : {}) });
    write({ ...rec, sent: true });
    return true;
  } catch (_) {
    return false;
  } finally {
    inFlight = false;
  }
}
